import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadAreaProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string;
  maxFiles?: number;
  className?: string;
  multiple?: boolean;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  onFilesSelected,
  acceptedFileTypes = "*",
  maxFiles = 10,
  className,
  multiple = true
}) => {
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    if (maxFiles && fileArray.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    onFilesSelected(fileArray);
    
    // Reset input
    if (event.target) {
      event.target.value = '';
    }
  }, [onFilesSelected, maxFiles]);

  return (
    <div className={cn("border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-muted-foreground/50 transition-colors", className)}>
      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm text-muted-foreground mb-4">
        Drag and drop files here, or click to select files
      </p>
      <Input
        type="file"
        multiple={multiple}
        accept={acceptedFileTypes}
        onChange={handleFileChange}
        className="hidden"
        id="course-file-upload"
      />
      <Button 
        type="button"
        variant="outline" 
        onClick={() => document.getElementById('course-file-upload')?.click()}
      >
        Choose Files
      </Button>
      <p className="text-xs text-muted-foreground mt-2">
        {acceptedFileTypes.includes('.csv') || acceptedFileTypes.includes('.xlsx') ? 
          'Supported: CSV, Excel (XLSX, XLS) files' : 
          'Supported: PDF, DOC, PPT, Excel, Video, Audio, Images, SCORM packages'
        }
      </p>
    </div>
  );
};